import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, Checkbox } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { useMutation } from '@tanstack/react-query';
import CloseIcon from '@mui/icons-material/Close';
import CommonModalButton from '../../../../form-components/CommonModalButton';
import {
  updateMemberTicketUseBranches,
  updateMemberPassUseBranches,
  updateMemberTrialUseBranches,
} from '../../../../api/ticket';
import { errorMessages } from '../../../../utils/methods';

const ModalContainer = styled(Box)({
  width: '480px',
  position: 'absolute',
  top: '50%',
  left: '50%',
  background: '#fff',
  borderRadius: '8px',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ModalHeader = styled(Box)({
  height: '56px',
  background: '#262626',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 32px',
});

const ModalTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  color: '#fff',
});

const CloseButton = styled(Box)({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': { color: '#fff', fontSize: '24px' },
});

const ModalContent = styled(Box)({
  padding: '28px 32px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const ProductName = styled(Typography)({
  fontSize: '15px',
  fontWeight: 600,
  color: '#262626',
  padding: '10px 14px',
  background: '#F7F8F9',
  borderRadius: '6px',
});

const BranchRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '2px 0',
});

const BranchName = styled(Typography)({
  fontSize: '15px',
  fontWeight: 500,
  color: '#262626',
});

const SaleBranchTag = styled(Typography)({
  fontSize: '12.5px',
  color: '#8C8C8C',
  marginLeft: '4px',
});

const NoticeText = styled(Typography)({
  fontSize: '12.5px',
  color: '#8C8C8C',
  lineHeight: 1.5,
  background: '#F7F8F9',
  borderRadius: '6px',
  padding: '12px 14px',
});

const ButtonWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  padding: '4px 32px 32px',
});

// 상품 종류별 저장 API. target.kind 로 고른다.
const SAVERS = {
  course: updateMemberTicketUseBranches,
  pass: updateMemberPassUseBranches,
  trial: updateMemberTrialUseBranches,
};

/**
 * 회원권 1건의 이용지점 수동 조정.
 * 후보는 백엔드가 내려준 branchShare.useBranchOptions(판매지점 + 상품 이용 참여지점)이고,
 * 구매(판매)지점도 끌 수 있다. 다만 전부 끄면 이 회원권을 어디서도 쓸 수 없게 되므로
 * 최소 한 곳은 남겨야 한다(백엔드도 같은 조건으로 막는다).
 */
const UseBranchEditModal = ({ open, target, memberId, token, onClose, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();

  const share = target?.item?.branchShare;
  const options = share?.useBranchOptions ?? [];
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (open && share) {
      setSelected(
        share.selectedUseBranchIds ?? (share.useBranchOptions ?? []).map(option => option.branchId),
      );
    }
  }, [open, share]);

  const saveMutation = useMutation({
    mutationFn: branchIds =>
      SAVERS[target.kind](memberId, target.ticketId, { branchIds }, token),
    onSuccess: () => {
      toast.success(t('이용지점이 저장되었습니다'));
      onSaved();
      onClose();
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  const toggle = branchId => {
    setSelected(prev =>
      prev.includes(branchId) ? prev.filter(id => id !== branchId) : [...prev, branchId],
    );
  };

  if (!open || !target) return null;

  return (
    <Modal open={open} onClose={onClose} disableEscapeKeyDown>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{t('이용지점 변경')}</ModalTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <ProductName>{target.productName}</ProductName>

          <Box>
            {options.map(option => (
              <BranchRow key={option.branchId}>
                <Checkbox
                  size="small"
                  checked={selected.includes(option.branchId)}
                  onChange={() => toggle(option.branchId)}
                  sx={{ color: '#C4CBD4', '&.Mui-checked': { color: '#0F7F85' } }}
                />
                <BranchName>{option.branchName}</BranchName>
                {option.saleBranch && <SaleBranchTag>구매지점</SaleBranchTag>}
              </BranchRow>
            ))}
          </Box>

          <NoticeText>
            {'상품에 등록된 이용가능지점 중에서만 켜고 끌 수 있습니다.'}
            <br />
            {'끈 지점에서는 이 회원권이 회원 목록·체크인에 보이지 않고 출입도 닫힙니다.'}
            <br />
            {'구매지점도 끌 수 있지만, 최소 한 곳은 남겨야 합니다.'}
          </NoticeText>
        </ModalContent>

        <ButtonWrapper>
          <CommonModalButton variant="cancel" onClick={onClose}>
            {t('취소')}
          </CommonModalButton>
          <CommonModalButton
            variant="submit"
            onClick={() => saveMutation.mutate(selected)}
            disabled={saveMutation.isPending || selected.length === 0}
          >
            {t('저장')}
          </CommonModalButton>
        </ButtonWrapper>
      </ModalContainer>
    </Modal>
  );
};

export default UseBranchEditModal;
